import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { updateUserProfile } from '../../store/slices/user.slice'

import {
  categoriesList,
  animesList,
  charactersWithQuotes,
} from '../../data/lists'
import { cloudinaryService } from '../../services/cloudinary.service'

export function EditProfile({ user, onClose }) {
  const dispatch = useDispatch()

  // תמונות ברירת מחדל
  const DEFAULT_PROFILE_IMAGE =
    'https://pub-60946b28fb374ff48942bac3163dd658.r2.dev/user-icon-default_olftzk.svg'
  const DEFAULT_COVER_IMAGE =
    'https://pub-60946b28fb374ff48942bac3163dd658.r2.dev/uploads/2025/05/coverimage-1746428211528.webp'

  // 1) state למידע טקסטואלי
  const [formData, setFormData] = useState({
    nickname: user.nickname || '',
    bio: user.bio || '',
    userTag: user.userTag || '',
    favoriteCategories: user.favoriteCategories.map((category) => {
      // המר את שם הקטגוריה לקוד
      const match = Object.entries(categoriesList).find(
        ([key, value]) => category === `${value} (${key})`
      )
      return match ? match[0] : category
    }),
    favoriteAnime: user.favoriteAnime || '',
    favoriteCharacter: user.favoriteCharacter || '',
    favoriteQuote: user.favoriteQuote || '',
  })

  // 2) state עבור בחירת קבצים (תמונת פרופיל / תמונת רקע)
  const [profileImageFile, setProfileImageFile] = useState(null)
  const [coverImageFile, setCoverImageFile] = useState(null)

  // 3) מציג/מסתיר אינדיקציה לטעינה (upload + submit)
  const [isLoading, setIsLoading] = useState(false)

  const handleDeleteProfileImage = () => {
    try {
      setFormData((prev) => ({ ...prev, image: DEFAULT_PROFILE_IMAGE }))
      setProfileImageFile(null)
      toast.success('תמונת הפרופיל הוחזרה לברירת המחדל. אל תשכח ללחוץ "שמור"!')
    } catch (err) {
      toast.error('שגיאה במחיקת תמונת הפרופיל!')
    }
  }

  const handleDeleteCoverImage = () => {
    try {
      setFormData((prev) => ({ ...prev, coverImage: DEFAULT_COVER_IMAGE }))
      setCoverImageFile(null)
      toast.success('תמונת הרקע הוחזרה לברירת המחדל. אל תשכח ללחוץ "שמור"!')
    } catch (err) {
      toast.error('שגיאה במחיקת תמונת הרקע!')
    }
  }

  // 4) עדכון הקטגוריות (נשאר כמו שהיה)
  const handleCategoryChange = (category) => {
    setFormData((prev) => {
      const isSelected = prev.favoriteCategories.includes(category)
      if (isSelected) {
        return {
          ...prev,
          favoriteCategories: prev.favoriteCategories.filter(
            (cat) => cat !== category
          ),
        }
      }
      if (prev.favoriteCategories.length < 3) {
        return {
          ...prev,
          favoriteCategories: [...prev.favoriteCategories, category],
        }
      }
      return prev
    })
  }

  // 5) שליחת הטופס
  const handleSubmit = async (e) => {
    e.preventDefault()
    const formattedCategories = formData.favoriteCategories.map(
      (key) => `${categoriesList[key]} (${key})`
    )
    const updatedData = { ...formData, favoriteCategories: formattedCategories }

    try {
      setIsLoading(true)

      if (profileImageFile) {
        const response = await cloudinaryService.uploadImage(profileImageFile, {
          type: 'user-img',
          relatedId: user._id,
        })
        updatedData.image = response.imgUrl
      }

      if (coverImageFile) {
        const response = await cloudinaryService.uploadImage(coverImageFile, {
          type: 'user-cover',
          relatedId: user._id,
        })
        updatedData.coverImage = response.imgUrl
      }

      await dispatch(updateUserProfile(updatedData)).unwrap()
      onClose()
    } catch (err) {
      console.error('Error updating profile or uploading images:', err)
      toast.error('שגיאה בעדכון הפרופיל או בהעלאת תמונות')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-80">
      <div className="rounded-lg shadow-lg w-11/12 max-w-lg md:max-w-4xl p-6 bg-gray-800 text-white relative max-h-screen overflow-y-auto">
        {/* אם יש טעינה, מציג Spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12" />
          </div>
        )}

        <h2 className="text-2xl mb-4 text-white text-center">עריכת פרופיל</h2>

        <form
          onSubmit={handleSubmit}
          className={`${isLoading ? 'opacity-50' : ''} flex flex-col gap-6`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* --- כינוי --- */}
            <div>
              <label className="block text-white">כינוי</label>
              <input
                type="text"
                name="nickname"
                value={formData.nickname}
                onChange={(e) =>
                  setFormData({ ...formData, nickname: e.target.value })
                }
                className="w-full border rounded px-3 py-2 bg-gray-700 text-white"
                required
              />
            </div>

            {/* --- ביוגרפיה --- */}
            <div>
              <label className="block text-white">ביוגרפיה</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={(e) =>
                  setFormData({ ...formData, bio: e.target.value })
                }
                className="w-full border rounded px-3 py-2 bg-gray-700 text-white resize-none"
                rows="3"
              />
            </div>

            {/* --- תמונת פרופיל --- */}
            <div>
              <label className="block text-white">תמונת פרופיל</label>
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={handleDeleteProfileImage}
                  className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 text-xs rounded"
                >
                  מחק
                </button>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={isLoading}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setProfileImageFile(e.target.files[0])
                  }
                }}
                className="w-full text-white mt-2"
              />
            </div>
            {/* --- תמונת רקע --- */}
            <div>
              <label className="block text-white">תמונת רקע</label>
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={handleDeleteCoverImage}
                  className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 text-xs rounded"
                >
                  מחק
                </button>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={isLoading}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setCoverImageFile(e.target.files[0])
                  }
                }}
                className="w-full text-white mt-2"
              />
            </div>

            {/* --- קטגוריות מועדפות --- */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-white">
                קטגוריות מועדפות (מקסימום 3)
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-700 p-2 rounded-md">
                {Object.entries(categoriesList).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 text-white"
                  >
                    <input
                      type="checkbox"
                      checked={formData.favoriteCategories.includes(key)}
                      onChange={() => handleCategoryChange(key)}
                      className="accent-red-500"
                      disabled={isLoading}
                    />
                    <span>
                      {label} ({key})
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* --- אנימה מועדפת --- */}
            <div>
              <label className="block text-white">אנימה מועדפת</label>
              <input
                type="text"
                name="favoriteAnime"
                value={formData.favoriteAnime}
                onChange={(e) =>
                  setFormData({ ...formData, favoriteAnime: e.target.value })
                }
                placeholder="הקלד או בחר אנימה..."
                className="w-full border rounded px-3 py-2 bg-gray-700 text-white"
                disabled={isLoading}
              />
            </div>

            {/* --- דמות מועדפת --- */}
            <div>
              <label className="block text-white">דמות מועדפת</label>
              <input
                type="text"
                name="favoriteCharacter"
                value={formData.favoriteCharacter}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    favoriteCharacter: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2 bg-gray-700 text-white"
                disabled={isLoading}
              />
            </div>

            {/* --- ציטוט מועדף --- */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-white">ציטוט מועדף</label>
              <input
                type="text"
                name="favoriteQuote"
                value={formData.favoriteQuote}
                onChange={(e) =>
                  setFormData({ ...formData, favoriteQuote: e.target.value })
                }
                className="w-full border rounded px-3 py-2 bg-gray-700 text-white"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* --- כפתורי פעולה --- */}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-4 py-2 bg-red-500 text-white rounded"
              disabled={isLoading}
            >
              ביטול
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded"
              disabled={isLoading}
            >
              שמור
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
